import { useState, useEffect, useRef } from 'react';
import { getAccessToken, API_ENDPOINTS } from '../../api';
import { uploadFileToS3 } from '../../utils/s3Upload';
import { compressImage } from '../../utils/imageCompress';
import { BLOG_CATEGORIES } from '../../constants/categories';
import RichTextEditor from '../common/RichTextEditor';
import { htmlToText } from '../../utils/sanitizeHtml';

function formatDate(dateStr) {
  if (!dateStr) return '';
  return dateStr.split('T')[0];
}

// Matches backend Blog model field names exactly
const EMPTY_FORM = {
  blogId: null,
  title: '',
  content: '',
  image: '',
  urlLink: '',
  author: 'Admin',
  category: '',
  createdOn: '',
};

function normalise(b) {
  return { ...b, blogId: b.blogId || b.id || b._id };
}

function BlogsSection() {
  const [blogs, setBlogs] = useState([]);
  const [loadingBlogs, setLoadingBlogs] = useState(true);
  const [blogForm, setBlogForm] = useState(EMPTY_FORM);
  const [blogImageFile, setBlogImageFile] = useState(null);
  const [blogUploading, setBlogUploading] = useState(false);
  const [isEditingBlog, setIsEditingBlog] = useState(false);
  const [savingBlog, setSavingBlog] = useState(false);
  const [filterCategory, setFilterCategory] = useState('All');
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const blogFileInputRef = useRef();

  useEffect(() => { fetchBlogs(filterCategory, null, true); }, [filterCategory]);

  async function fetchBlogs(category, lek, replace = false) {
    replace ? setLoadingBlogs(true) : setLoadingMore(true);
    try {
      const params = new URLSearchParams({ limit: 10 });
      if (category && category !== 'All') params.set('category', category);
      if (lek) params.set('lastEvaluatedKey', JSON.stringify(lek));
      const response = await fetch(`${API_ENDPOINTS.BLOGS}?${params}`);
      if (response.ok) {
        const data = await response.json();
        const newItems = (data.blogs || data).map(normalise);
        setBlogs(prev => replace ? newItems : [...prev, ...newItems]);
        setLastEvaluatedKey(data.lastEvaluatedKey || null);
        setHasMore(!!data.lastEvaluatedKey);
      }
    } catch (err) {
      console.error('Failed to fetch blogs:', err);
    } finally {
      replace ? setLoadingBlogs(false) : setLoadingMore(false);
    }
  }

  function handleCategoryFilter(category) {
    setFilterCategory(category);
    setBlogs([]);
    setLastEvaluatedKey(null);
    setHasMore(true);
  }

  function loadMore() {
    fetchBlogs(filterCategory, lastEvaluatedKey);
  }

  const handleBlogInputChange = (e) => {
    setBlogForm({ ...blogForm, [e.target.name]: e.target.value });
  };

  const handleBlogFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const compressed = await compressImage(file, 100);
    setBlogImageFile(compressed);
  };

  const saveBlogToAPI = async (blog) => {
    setSavingBlog(true);
    try {
      const isUpdate = Boolean(blog.blogId);
      const url = isUpdate
        ? `${API_ENDPOINTS.BLOGS}/${encodeURIComponent(blog.category)}/${blog.blogId}`
        : API_ENDPOINTS.BLOGS;
      const response = await fetch(url, {
        method: isUpdate ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify(blog),
      });
      if (!response.ok) throw new Error(`Failed to save blog (${response.status})`);
      const raw = await response.json();
      setSavingBlog(false);
      return { success: true, data: normalise(raw) };
    } catch (error) {
      setSavingBlog(false);
      return { success: false, error: error.message };
    }
  };

  const handleAddBlog = async () => {
    if (!blogForm.title || !blogForm.content || !blogForm.category) {
      alert('Please fill in Title, Category, and Content');
      return;
    }
    let imageUrl = blogForm.image;
    if (blogImageFile) {
      setBlogUploading(true);
      try { imageUrl = await uploadFileToS3(blogImageFile, 'blogs'); }
      catch (err) { alert('Image upload failed: ' + err.message); setBlogUploading(false); return; }
      setBlogUploading(false);
    }
    // Omit blogId — backend assigns it on creation
    const { blogId: _id, ...payload } = {
      ...blogForm,
      image: imageUrl,
      createdOn: new Date().toISOString().split('T')[0],
    };
    const result = await saveBlogToAPI(payload);
    if (result.success) {
      setBlogs([result.data, ...blogs]);
      setBlogForm(EMPTY_FORM);
      setBlogImageFile(null);
      if (blogFileInputRef.current) blogFileInputRef.current.value = '';
      alert('Blog saved successfully!');
    } else {
      alert('Failed to save: ' + result.error);
    }
  };

  const handleEditBlog = (blog) => {
    setBlogForm({
      ...EMPTY_FORM,
      ...blog,
      urlLink: blog.urlLink || '',
      image: blog.image || '',
    });
    setIsEditingBlog(true);
  };

  const handleUpdateBlog = async () => {
    if (!blogForm.category) { alert('Please select a category'); return; }
    let imageUrl = blogForm.image;
    if (blogImageFile) {
      setBlogUploading(true);
      try { imageUrl = await uploadFileToS3(blogImageFile, 'blogs'); }
      catch (err) { alert('Image upload failed: ' + err.message); setBlogUploading(false); return; }
      setBlogUploading(false);
    }
    const result = await saveBlogToAPI({ ...blogForm, image: imageUrl });
    if (result.success) {
      setBlogs(blogs.map(b => b.blogId === blogForm.blogId ? result.data : b));
      setBlogForm(EMPTY_FORM);
      setBlogImageFile(null);
      if (blogFileInputRef.current) blogFileInputRef.current.value = '';
      setIsEditingBlog(false);
      alert('Blog updated successfully!');
    } else {
      alert('Failed to update: ' + result.error);
    }
  };

  const handleDeleteBlog = async (blogId, category) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;
    setSavingBlog(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.BLOGS}/${encodeURIComponent(category)}/${blogId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getAccessToken()}` },
      });
      if (!response.ok) throw new Error(`Failed to delete blog (${response.status})`);
      setBlogs(blogs.filter(b => b.blogId !== blogId));
    } catch (err) {
      alert('Failed to delete blog: ' + err.message);
    } finally {
      setSavingBlog(false);
    }
  };

  const handleCancelBlog = () => {
    setBlogForm(EMPTY_FORM);
    setBlogImageFile(null);
    if (blogFileInputRef.current) blogFileInputRef.current.value = '';
    setIsEditingBlog(false);
  };

  const inputStyle = { padding: 10, borderRadius: 8, border: '1px solid #3b82f6', fontSize: '0.95rem' };

  return (
    <div style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', padding: 24, borderRadius: 16, boxShadow: '0 2px 16px rgba(59,130,246,0.2)', marginBottom: 32, border: '2px solid #3b82f6' }}>
      <h2 style={{ color: '#1d4ed8', marginBottom: 16 }}>📝 Manage Blogs</h2>
      <p style={{ color: '#1e40af', fontSize: '0.9rem', marginBottom: 16 }}>Write and manage blog posts for your website.</p>

      {/* Blog Form */}
      <div style={{ background: '#fff', padding: 20, borderRadius: 12, marginBottom: 20 }}>
        <h3 style={{ marginBottom: 16, color: '#1e40af' }}>{isEditingBlog ? 'Edit Blog' : 'Write New Blog'}</h3>

        <div className="admin-form-grid-blogs">
          <input name="title" placeholder="Blog Title *" value={blogForm.title} onChange={handleBlogInputChange} style={inputStyle} />

          <select name="category" value={blogForm.category} onChange={handleBlogInputChange}
            style={{ ...inputStyle, color: blogForm.category ? '#1e293b' : '#94a3b8' }}>
            <option value="">Select Category *</option>
            {BLOG_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>

          <input name="author" placeholder="Author" value={blogForm.author} onChange={handleBlogInputChange} style={inputStyle} />

          {/* urlLink maps to backend urlLink field */}
          <input name="urlLink" placeholder="Reference URL (optional)" value={blogForm.urlLink} onChange={handleBlogInputChange} style={inputStyle} />

          {/* Thumbnail image upload */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, gridColumn: '1 / -1' }}>
            <label style={{ fontSize: '0.8rem', color: '#3b82f6', fontWeight: 600 }}>Thumbnail Image</label>
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" ref={blogFileInputRef} onChange={handleBlogFileChange}
              style={{ padding: 8, borderRadius: 8, border: '1px solid #3b82f6', fontSize: '0.9rem' }} />
            {blogImageFile && <span style={{ fontSize: '0.8rem', color: '#1d4ed8' }}>Selected: {blogImageFile.name}</span>}
            {blogUploading && <span style={{ color: '#3b82f6', fontSize: '0.85rem' }}>Uploading...</span>}
          </div>
        </div>

        {/* Full Content */}
        <div style={{ marginTop: 12 }}>
          <RichTextEditor
            value={blogForm.content}
            onChange={(html) => setBlogForm(prev => ({ ...prev, content: html }))}
            placeholder="Full blog content *"
            folder="blogs"
            minHeight={320}
          />
        </div>

        <div style={{ marginTop: 14, display: 'flex', gap: 10 }}>
          {isEditingBlog ? (
            <>
              <button onClick={handleUpdateBlog} disabled={savingBlog}
                style={{ padding: '10px 20px', borderRadius: 8, background: '#3b82f6', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
                {savingBlog ? 'Saving...' : 'Update Blog'}
              </button>
              <button onClick={handleCancelBlog}
                style={{ padding: '10px 20px', borderRadius: 8, background: '#e5e7eb', color: '#374151', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </>
          ) : (
            <button onClick={handleAddBlog} disabled={savingBlog}
              style={{ padding: '10px 20px', borderRadius: 8, background: '#3b82f6', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
              {savingBlog ? 'Saving...' : '+ Publish Blog'}
            </button>
          )}
        </div>
      </div>

      {/* Blogs List */}
      <div style={{ background: '#fff', padding: 16, borderRadius: 12 }}>
        <h3 style={{ marginBottom: 12, color: '#1e40af' }}>Published Blogs ({blogs.length})</h3>

        {/* Category Filter */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          {['All', ...BLOG_CATEGORIES].map(cat => (
            <button key={cat} onClick={() => handleCategoryFilter(cat)}
              style={{
                padding: '6px 14px', borderRadius: 20, border: 'none', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer',
                background: filterCategory === cat ? '#3b82f6' : '#eff6ff',
                color: filterCategory === cat ? '#fff' : '#1d4ed8',
              }}>
              {cat}
            </button>
          ))}
        </div>

        {loadingBlogs && <p style={{ color: '#3b82f6' }}>Loading blogs...</p>}
        {!loadingBlogs && blogs.length === 0 && (
          <p style={{ textAlign: 'center', color: '#1e40af' }}>No blogs yet. Write your first blog above!</p>
        )}
        {!loadingBlogs && blogs.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {blogs.map(blog => (
              <div key={blog.blogId} className="admin-list-item">
                {blog.image ? (
                  <img
                    src={blog.image}
                    alt={blog.title}
                    onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
                    style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8, background: '#dbeafe', flexShrink: 0 }}
                  />
                ) : null}
                <div style={{
                  display: blog.image ? 'none' : 'flex',
                  width: 60, height: 60, borderRadius: 8, background: '#dbeafe',
                  alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.5rem', flexShrink: 0,
                }}>
                  📝
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: '#1e40af', fontSize: '1rem' }}>{blog.title}</div>
                  <div style={{ fontSize: '0.8rem', color: '#3b82f6', marginBottom: 4 }}>
                    {blog.category} • {blog.author} • {formatDate(blog.createdOn)}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {htmlToText(blog.content)}
                  </div>
                  {blog.urlLink && (
                    <div style={{ fontSize: '0.75rem', color: '#6366f1', marginTop: 4 }}>🔗 {blog.urlLink}</div>
                  )}
                </div>
                <div className="admin-list-item-actions">
                  <button onClick={() => handleEditBlog(blog)}
                    style={{ padding: '6px 12px', borderRadius: 6, background: '#3b82f6', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>
                    Edit
                  </button>
                  <button onClick={() => handleDeleteBlog(blog.blogId, blog.category)} disabled={savingBlog}
                    style={{ padding: '6px 12px', borderRadius: 6, background: '#f43f5e', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: 12 }}>
                <button onClick={loadMore} disabled={loadingMore}
                  style={{ padding: '10px 28px', borderRadius: 8, background: '#eff6ff', color: '#1d4ed8', border: '2px solid #3b82f6', fontWeight: 700, fontSize: '0.9rem', cursor: loadingMore ? 'not-allowed' : 'pointer', opacity: loadingMore ? 0.7 : 1 }}>
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default BlogsSection;
